import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config/api";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const LeadsList = () => {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusSearchTerm, setStatusSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch leads data
  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = () => {
    setLoading(true);
    fetch(`${BASE_URL}leads?staff_id=1`)
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 1) {
          setLeads(data.data);
          setFilteredLeads(data.data);
        } else {
          console.error("Failed to fetch leads:", data.message);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching leads:", error);
        setLoading(false);
      });
  };

  // Filter leads function
  const filterLeads = () => {
    const filtered = leads.filter((lead) => {
      const matchesSearch = lead.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusSearchTerm === "" ||
        lead.status?.toLowerCase() === statusSearchTerm.toLowerCase();

      // Date filtering
      const leadDate = new Date(lead.followup_date);
      const formattedLeadDate = new Date(leadDate.setHours(0, 0, 0, 0));
      const formattedStartDate = startDate
        ? new Date(new Date(startDate).setHours(0, 0, 0, 0))
        : null;
      const formattedEndDate = endDate
        ? new Date(new Date(endDate).setHours(0, 0, 0, 0))
        : null;

      const matchesDate =
        (!formattedStartDate || formattedLeadDate >= formattedStartDate) &&
        (!formattedEndDate || formattedLeadDate <= formattedEndDate);

      return matchesSearch && matchesStatus && matchesDate;
    });

    setFilteredLeads(filtered);
  };

  // Handle search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handle status dropdown change
  const handleStatusSearchChange = (event) => {
    setStatusSearchTerm(event.target.value);
  };

  // Handle date changes with auto-filter
  const handleDateChange = (field, value) => {
    if (field === "start") {
      setStartDate(value);
    } else {
      setEndDate(value);
    }
  };

  // Apply filters
  const handleFilter = () => {
    filterLeads();
  };

  // Delete lead
  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this lead?")) {
      fetch(`${BASE_URL}leads/${id}`, { method: "DELETE" })
        .then((response) => response.json())
        .then((data) => {
          if (data.status === 1) {
            const updatedLeads = leads.filter((lead) => lead.id !== id);
            setLeads(updatedLeads);
            setFilteredLeads(updatedLeads);
            alert("Lead deleted successfully!");
          } else {
            alert("Failed to delete the lead.");
          }
        })
        .catch((error) => console.error("Error deleting lead:", error));
    }
  };

  const handleEdit = (id) => {
    navigate(`/EditLead/${id}`);
  };

  const handleAdd = () => {
    navigate("/AddLeads");
  };

  // Export to PDF
  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Leads Report", 14, 15);

    const tableData = filteredLeads.map((lead) => [
      lead.name || "N/A",
      lead.number || "N/A",
      formatDate(lead.followup_date),
      lead.description || "N/A",
      lead.status || "N/A",
    ]);

    doc.autoTable({
      head: [["Name", "Number", "Follow-Up Date", "Description", "Status"]],
      body: tableData,
      startY: 20,
    });
    doc.save("leads_report.pdf");
  };

  // Export to Excel
  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredLeads.map((lead) => ({
        Name: lead.name || "N/A",
        Number: lead.number || "N/A",
        "Follow-Up Date": formatDate(lead.followup_date),
        Description: lead.description || "N/A",
        Status: lead.status || "N/A",
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leads Report");
    XLSX.writeFile(workbook, "leads_report.xlsx");
  };

  // Format Date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Status Modal functions
  const openStatusModal = (id) => {
    setSelectedLeadId(id);
    setStatusModalOpen(true);
  };

  const closeStatusModal = () => {
    setStatusModalOpen(false);
  };

  // Update lead status
  const handleStatusChange = (status) => {
    fetch(`${BASE_URL}leads/status/${selectedLeadId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 1) {
          const updatedLeads = leads.map((lead) => {
            if (lead.id === selectedLeadId) {
              return { ...lead, status: data.data.status };
            }
            return lead;
          });

          setLeads(updatedLeads);
          setFilteredLeads(updatedLeads);
          closeStatusModal();
          alert("Status updated successfully!");
        } else {
          alert("Failed to update lead status.");
        }
      })
      .catch((error) => {
        console.error("Error updating lead status:", error);
        alert("Error updating lead status.");
      });
  };

  if (loading) {
    return (
      <div id="main">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6 order-md-1 order-last">
              <h3>Sales Lead List</h3>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row" id="basic-table">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-6">
                    <h5 className="card-title">
                      Total Leads:{" "}
                      <span className="text-primary">
                        {filteredLeads.length}
                      </span>
                    </h5>
                  </div>
                  <div className="col-md-6 text-end">
                    <button
                      className="btn btn-primary me-2"
                      onClick={handleAdd}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Add Lead
                    </button>
                    <button
                      className="btn btn-secondary me-2"
                      onClick={handleExportPDF}
                    >
                      <i className="bi bi-file-pdf me-2"></i>
                      Export PDF
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={handleExportExcel}
                    >
                      <i className="bi bi-file-excel me-2"></i>
                      Export Excel
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                {/* Filters */}
                <div className="row mb-4">
                  <div className="col-md-3">
                    <label className="form-label">Search by Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search lead name"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Filter by Status</label>
                    <select
                      className="form-control"
                      value={statusSearchTerm}
                      onChange={handleStatusSearchChange}
                    >
                      <option value="">All Status</option>
                      <option value="completed">Completed</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Start Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={startDate}
                      onChange={(e) =>
                        handleDateChange("start", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">End Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => handleDateChange("end", e.target.value)}
                    />
                  </div>
                  <div className="col-md-12 mt-3">
                    <button
                      className="btn btn-secondary"
                      onClick={handleFilter}
                    >
                      <i className="bi bi-funnel me-2"></i>
                      Apply Filters
                    </button>
                  </div>
                </div>

                {/* Leads Table */}
                <div className="table-responsive">
                  <table className="table table-lg table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th>NAME</th>
                        <th>NUMBER</th>
                        <th>FOLLOW-UP DATE</th>
                        <th>DESCRIPTION</th>
                        <th>STATUS</th>
                        <th className="text-end">ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLeads.length > 0 ? (
                        filteredLeads.map((lead) => (
                          <tr key={lead.id}>
                            <td className="fw-bold">{lead.name || "N/A"}</td>
                            <td>{lead.number || "N/A"}</td>
                            <td>{formatDate(lead.followup_date)}</td>
                            <td>{lead.description || "N/A"}</td>
                            <td>
                              <span
                                className={`badge ${
                                  lead.status === "completed"
                                    ? "bg-success"
                                    : "bg-warning"
                                }`}
                              >
                                {lead.status || "N/A"}
                              </span>
                            </td>
                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-success me-2"
                                onClick={() => handleEdit(lead.id)}
                                title="Edit"
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-warning me-2"
                                onClick={() => openStatusModal(lead.id)}
                                title="Change Status"
                              >
                                <i className="bi bi-arrow-repeat"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(lead.id)}
                                title="Delete"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-4">
                            <div className="text-muted">
                              <i className="bi bi-search display-4"></i>
                              <p className="mt-2">No leads found</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Status Modal */}
      {statusModalOpen && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Change Lead Status</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeStatusModal}
                ></button>
              </div>
              <div className="modal-body text-center">
                <p className="mb-3">Select new status for this lead:</p>
                <div className="d-flex justify-content-center gap-3">
                  <button
                    className="btn btn-success"
                    onClick={() => handleStatusChange("completed")}
                  >
                    <i className="bi bi-check-circle me-2"></i>
                    Completed
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={() => handleStatusChange("pending")}
                  >
                    <i className="bi bi-clock me-2"></i>
                    Pending
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsList;
